import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { debounce } from "lodash";
import { useLazyQuery, useMutation } from "@apollo/client";

import { useQueryParams } from "../../../../../hooks/common/useQueryParams";

import { GET_INVENTORY, LIST_INVENTORIES } from "../../../../../GraphQL/Queries/inventories";
import { UPDATE_INVENTORY, CREATE_INVENTORY, CONVERT_INVENTORY_TO_VEHICLE } from "../../../../../GraphQL/Mutations/inventories";

import {
  inventoryFormDefaultValues,
  parseLegacyVehicleInfo,
  parseCreateInventoryInput,
  parseInventoryToLegacyVehicleForm,
  parseInventoryToInventoryForm,
} from "./helpers";

export const useInventories = ({ userSession, history, match, setVehicleForm, loadDivisions, loadVMHs, location }) => {
  const [inventoryForm, setInventoryForm] = useState(inventoryFormDefaultValues);
  const [inventories, setInventories] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState(null);
  const [searchInventoriesResults, setSearchInventoriesResults] = useState([]);

  const [imageName, setImageName] = useState('');
  const [imageThumbnail, setImageThumbnail] = useState(null);
	const [loadingImage, setLoadingImage] = useState(false);
  const [specName, setSpecName] = useState('');
  const [specThumbnail, setSpecThumbnail] = useState(null);
	const [loadingSpec, setLoadingSpec] = useState(false);

  const [importedOrdersFileName, setImportedOrdersFileName] = useState('');
  const [loadingImportOrders, setLoadingImportOrders] = useState(false);

  const { query, setParams } = useQueryParams({ location, history });

  const [createInventoryMutation, { data: createdInventory, loading: createInventoryLoading, error: createInventoryError }] = useMutation(CREATE_INVENTORY);
  const [updateInventoryMutation, { data: updateResponse, loading: updateInventoryLoading, error: updateError }] = useMutation(UPDATE_INVENTORY);
  const [convertInventoryToVehicleMutation, { data: convertedVehicle, loading: convertVehicleLoading, error: convertVehicleError }] = useMutation(CONVERT_INVENTORY_TO_VEHICLE);

  const inventoryCreatedBy = userSession.user?.email;
  const inventoryId = match?.params?.inventoryId;
  const hostName = window.location.hostname.replace('www.', '');
  const currentUrl = hostName === 'localhost' ? 'testing.thedvms.com' : hostName;

  // TODO: if changed here should also change in the nav bar validation to show the order status link
  const subaruStakeholderId = 1017;
  const hasSubaruAccess = userSession.user?.profile_access?.active_sh_access?.find(({ stakeholderId }) => stakeholderId === subaruStakeholderId);
  const userIsEwelina = userSession.user?.email === 'ewelina.flair@integratedauto.com';
  const clientCode = hasSubaruAccess || userIsEwelina ? 'Subaru' : null;

  const [getInventories, { data: getInventoriesData, loading: getListLoading }] = useLazyQuery(LIST_INVENTORIES, {
    variables: {
      clientCode
    },
    errorPolicy: 'all'
  });

  const [getInventory, { data: getInventoryData, loading: getLoading }] = useLazyQuery(GET_INVENTORY, {
    variables: {
      clientCode,
      id: inventoryId
    },
    errorPolicy: 'all'
  });

  useEffect(() => {
    if (inventoryId) getInventory();
  }, [inventoryId]);

  useEffect(() => {
    const inventory = getInventoryData?.getInventory || null;

    if (inventory && inventory.orderNo !== inventoryForm.orderNo) {
      const currentOemId = parseInt(inventory.vehicleInfo?.oemId || userSession.user?.current_oem_id);
      const currentShId = parseInt(inventory.vehicleInfo?.shId || userSession.user?.current_sh_id);

      if (currentOemId && currentShId) {
        loadDivisions(currentOemId);
        loadVMHs(currentShId);

        const parsedVehicleForm = parseInventoryToLegacyVehicleForm(inventory, currentOemId, currentShId);
        const parsedInventoryForm = parseInventoryToInventoryForm(inventory);

        setVehicleForm(previousState => ({ ...previousState, ...parsedVehicleForm }));
        setInventoryForm(previousState => ({ ...previousState, ...parsedInventoryForm }));
      }
    }
  }, [getInventoryData]);

  useEffect(() => {
    if (clientCode) {
      getInventories().then(() => {
        if (query.searchTerm) {
          setSearchTerm(query.searchTerm);
          return; // keep loading true until search results are set.
        }

        setLoading(false);
      }).catch(() => {
        setLoading(false)
      });

      return;
    }

    setLoading(false);
  }, [])

  useEffect(() => {
    if (getInventoriesData) {
      const inventoriesList = getInventoriesData?.listInventories?.inventories?.length ? getInventoriesData.listInventories.inventories : [];

      setInventories(inventoriesList);
      setSearchInventoriesResults(inventoriesList);
    }
  }, [getInventoriesData]);

  const searchInventories = useCallback(
    debounce(() => {
      if (typeof searchTerm !== 'string') return;

      if (searchTerm !== query.searchTerm) {
        setParams([
          { name: 'searchTerm', value: searchTerm }
        ]);
      }

      if (searchTerm.length > 2) {
        setSearching(true);

        const filteredInventories = inventories.filter(({ modelCode, vin, orderNo }) => {
          // const vehicleName = displayName.toLowerCase();
          const vehicleModelCode = modelCode?.toLowerCase() || '';
          const vehicleVIN = vin?.toLowerCase() || '';
          const vehicleOrderNo = orderNo?.toLowerCase() || '';

          return vehicleModelCode.includes(searchTerm.toLowerCase())
            || vehicleVIN.includes(searchTerm.toLowerCase())
            || vehicleOrderNo.includes(searchTerm.toLowerCase());
            // || vehicleName.includes(searchTerm.toLowerCase())
        });

        setSearchInventoriesResults(filteredInventories.length ? filteredInventories : []);
        setSearching(false);
        setLoading(false);
      } else {
        setSearchInventoriesResults(inventories);
        setLoading(false);
      }
    }, 300),
    [searchTerm, inventories]
  );

  useEffect(() => {
    searchInventories();
  }, [searchTerm, inventories]);

  const updateInventoryForm = (event) => {
    const { value, name } = event.target;
    setInventoryForm(previousState => ({ ...previousState, [name]: value }));
  }

  const checkRequiredFields = (inventoryMutationInput) => {
    const { vehicleInfo, vehicleType, orderNo, modelTrim, dealerId } = inventoryMutationInput;

    if (!vehicleInfo.year || !vehicleInfo.make || !vehicleType || !orderNo || !modelTrim || !dealerId) {
      showStatusMsg('Please fill out all required fields', 'error');
      // setLoadingSubmit(false);
      return false;
    }

    return true;
  }

  const createInventory = async (vehicle, inventory) => {
    const parsedLegacyVehicle = parseLegacyVehicleInfo(vehicle);
    const createInventoryMutationInput = parseCreateInventoryInput(vehicle, parsedLegacyVehicle, inventory, clientCode, inventoryCreatedBy);

    const requiredFieldsFilled = checkRequiredFields(createInventoryMutationInput);
    if (!requiredFieldsFilled) return;

    try {
      const createdInventoryResponse = await createInventoryMutation({ variables: { input: createInventoryMutationInput } });
      const { id: createdInventoryId } = createdInventoryResponse.data.createInventory.inventory;

      showStatusMsg('Order created successfully', 'success');
      window.history.back();
    } catch (error) {
      console.log('error :>> ', error);
    }
  };

  const updateInventory = async (vehicle, inventory) => {
    const parsedLegacyVehicle = parseLegacyVehicleInfo(vehicle);

    const updateInventoryMutationInput = {
      ...parseCreateInventoryInput(vehicle, parsedLegacyVehicle, inventory),
			id: inventory.id,
			updatedBy: inventoryCreatedBy,
		};

    try {
      const updatedInventoryResponse = await updateInventoryMutation({ variables: { input: updateInventoryMutationInput } });

      showStatusMsg('Order updated successfully', 'success');
      // history.push(`/vehicleoverview/order/edit/${createdInventoryId}`);
    } catch (error) {
      console.log('error :>> ', error);
    }
  }

  const updateInventoryInline = (input) => updateInventoryMutation({ variables: { input } });

  const convertInventoryToVehicle = (vehicle, inventory) => async (vehicleId, legacyApiVehicleId) => {
    const parsedLegacyVehicle = parseLegacyVehicleInfo(vehicle);
    const parsedCreateInput = parseCreateInventoryInput(vehicle, parsedLegacyVehicle, inventory);

    const updateInventoryInput = {
      ...parsedCreateInput,
      id: inventory.id,
      updatedBy: inventoryCreatedBy,
      vehicleId: `${vehicleId}`,
      additionalData: [
        ...parsedCreateInput.additionalData,
        { name: 'legacyApiVehicleId', value: `${legacyApiVehicleId}` }, // this is here to open the vehicle details page after converting
      ],
    };

    const {
      dealerId, locationStatus, vehicleType, modelTrim, color, orderNo, offlineDate, fhiDeparture, vesselEta, // remove order fields for convertInventoryToVehicleMutation
      ...convertInventoryToVehicleInput
    } = updateInventoryInput;

    try {
      const convertedInventoryToVehicleResponse = await convertInventoryToVehicleMutation({ variables: { input: convertInventoryToVehicleInput } });
    } catch (error) {
      console.log('error :>> ', error);
    }
  }

	const uploadImage = (files) => {
		const [file] = files;
    if (!file) return;

    setLoadingImage(true);

    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      showStatusMsg('Incorrect filetype for Vehicle Image. Please upload a .jpg, .jpeg, or .png file', 'error');
      setLoadingImage(false);
      return;
    }

    if (file.size / 1000 > 8000) {
      showStatusMsg('Maximum file size is 8 MB. Please compress your image and try again.', 'error');
      setLoadingImage(false);
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    const uploadUrl = `https://api.${currentUrl}/vehicle_image_uploads`
    axios.post(uploadUrl, formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(({ data }) => {
      setInventoryForm(previousState => ({ ...previousState, photoId: data.fileId }));

      setImageName(file.name);
      setImageThumbnail(data.signedUrl);

      setLoadingImage(false);
    }).catch((error) => {
      console.log('uploading image error: ', error);
      showStatusMsg('There was an error uploading your image. Please try again.', 'error');

      setLoadingImage(false);
    });
  }

	const uploadSpecSheet = (files) => {
    const [file] = files;
    if (!file) return;

		setLoadingSpec(true);

    const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
        showStatusMsg('Incorrect filetype for Spec Sheet. Please upload a .doc, .docx, or .pdf file', 'error');
        setLoadingSpec(false);
        return;
    }

    const formData = new FormData();
    formData.append("file", file);

    const uploadUrl = `https://api.${currentUrl}/vehicle_spec_uploads`;
    axios.post(uploadUrl, formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(({ data }) => {
      setInventoryForm(previousState => ({ ...previousState, specSheetId: data.fileId }));

      setSpecName(file.name);
      setSpecThumbnail(data.signedUrl);

      setLoadingSpec(false);
    }).catch((error) => {
      console.log('uploading spec sheet error: ', error);
      showStatusMsg('There was an error uploading your spec sheet. Please try again.', 'error');

      setLoadingSpec(false);
    });
  }

  const importOrders = (files) => {
    const [file] = files;
    if (!file) return;

    setLoadingImportOrders(true);

    const allowedTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
    if (!allowedTypes.includes(file.type)) {
      showStatusMsg('Incorrect filetype for Import. Please upload a .xlsx or .xls file', 'error');
      return;
    }

    const formData = new FormData();
    formData.append("file", file);

    const uploadUrl = `https://api.${currentUrl}/inventories/upload`;
    axios.post(uploadUrl, formData, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`,
        'Content-Type': 'multipart/form-data'
      }
    })
    .then(({ data }) => {
      setImportedOrdersFileName(file.name);
      // showStatusMsg('Orders imported successfully', 'success');
    }).catch((error) => {
      console.log('importing orders error: ', error);
      showStatusMsg('There was an error importing your orders. Please try again.', 'error');
    }).finally(() => setLoadingImportOrders(false));
  }

  return {
    clientCode,
    inventoryId,
    getInventories,
    inventoryForm,
    updateInventoryForm,
    createInventory,
    inventories: searchInventoriesResults,
    isLoadingInventoriesList: getListLoading || loading,
    isLoadingInventoryMutation: createInventoryLoading || updateInventoryLoading,
    updateInventoryInline,
    updateInventory,
    updateResponse,
    updateError,
    searching,
    searchTerm,
    setSearchTerm,

    uploadImage,
    loadingImage,
    imageThumbnail,
    imageName,
    uploadSpecSheet,
    loadingSpec,
    specThumbnail,
    specName,

    importOrders,
    importedOrdersFileName,
    loadingImportOrders,

    convertInventoryToVehicle,
  };
};
